import { useNavigationParams } from '../navigation/history'
import { useEffect, useState } from 'react'
import { useAuthSession } from '../../lib/authSession'
import { capabilities } from './api'

import type { DocumentRef } from '../../lib/documentReader'
import type { RecordContents, RtSegment } from '../RecordTable'
import type { MatrixRow } from '../fund/fundArea'
import RecordEditForm from './RecordEditForm'
import type { EditScope, EditTarget } from './contract'

export default function RecordEditTargets({
  segment,
  table,
  matrix,
  scope,
  onRecordChanged,
}: {
  segment: RtSegment
  table: RecordContents
  matrix: MatrixRow[]
  scope: EditScope | null
  onRecordChanged?: () => void
}) {
  const [params, setParams] = useNavigationParams()
  const session = useAuthSession()
  const [enabled, setEnabled] = useState<boolean | null>(null)
  useEffect(() => {
    let active = true
    setEnabled(null)
    capabilities()
      .then((value) => {
        if (active) setEnabled(value.record_edits_enabled ?? null)
      })
      .catch(() => {})
    return () => {
      active = false
    }
  }, [session])
  const positions = new Set([segment.position_id, ...segment.position_ids])
  const targets: EditTarget[] = [
    ...table.positions
      .filter((row) => positions.has(row.id) && row.state !== 'proposed')
      .map((row) => ({
        kind: 'position' as const,
        id: row.id,
        label: `${row.position_role === 'single' ? 'Position' : row.position_role === 'undrawn' ? 'Undrawn position' : 'Commitment position'} · ${row.holder_name ?? segment.holder_name} · ${row.instrument_name ?? segment.instrument_name}`,
      })),
    ...table.instruments
      .filter((row) => row.id === segment.instrument_id && row.state !== 'proposed')
      .map((row) => ({
        kind: 'instrument' as const,
        id: row.id,
        label: `Instrument · ${row.name}`,
      })),
    ...table.entities
      .filter(
        (row) => [segment.holder_id, segment.issuer_id].includes(row.id) && row.state !== 'proposed'
      )
      .map((row) => ({
        kind: 'entity' as const,
        id: row.id,
        label: `${row.id === segment.holder_id ? 'Holder' : 'Issuer'} · ${row.name}`,
      })),
    ...table.trades
      .filter(
        (row) => row.position_id && positions.has(row.position_id) && row.state !== 'proposed'
      )
      .map((row) => ({
        kind: 'trade' as const,
        id: row.id,
        label: `${row.transaction_type.replace(/_/g, ' ')} · ${row.effective_date ?? 'Date not recorded'}`,
      })),
  ]
  const selected = params.get('record_edit_target')
  const target = targets.find((row) => `${row.kind}:${row.id}` === selected)
  function choose(next: EditTarget) {
    setParams(
      (current) => {
        const updated = new URLSearchParams(current)
        updated.set('record_edit_target', `${next.kind}:${next.id}`)
        updated.set('record_edit_position', segment.position_id)
        updated.delete('record_edit')
        return updated
      },
      { replace: false }
    )
  }
  const docs = new Map<string, DocumentRef>()
  if (target) {
    for (const row of matrix)
      if (row.object_type === target.kind && row.object_id === target.id && row.document_id)
        docs.set(row.document_id, {
          document_id: row.document_id,
          display_title: row.document_name,
          page: row.page,
        })
    const trade = table.trades.find((row) => target.kind === 'trade' && row.id === target.id)
    if (trade?.document_ref) docs.set(trade.document_ref.document_id, trade.document_ref)
    else if (trade?.document_id) docs.set(trade.document_id, { document_id: trade.document_id })
  }
  if (!selected && enabled !== true) return null
  if (!scope) return <p className="nav-muted">Choose a fund context to edit this record.</p>
  return (
    <div className="record-edit-target-section">
      <div className="record-edit-targets" aria-label="Edit an object in this position">
        {targets.map((row) => (
          <button
            key={`${row.kind}:${row.id}`}
            onClick={() => choose(row)}
            aria-pressed={selected === `${row.kind}:${row.id}`}
          >
            Edit {row.label}
          </button>
        ))}
      </div>
      {target ? (
        <RecordEditForm
          key={`${target.kind}:${target.id}`}
          target={target}
          scope={scope}
          documents={[...docs.values()]}
          initialEditId={params.get('record_edit')}
          onRecordChanged={onRecordChanged}
          onNewCorrection={() =>
            setParams(
              (current) => {
                const updated = new URLSearchParams(current)
                updated.delete('record_edit')
                return updated
              },
              { replace: false }
            )
          }
          onResult={(result) =>
            setParams(
              (current) => {
                const updated = new URLSearchParams(current)
                updated.set('record_edit', result.id)
                return updated
              },
              { replace: true }
            )
          }
        />
      ) : null}
    </div>
  )
}
